import type { OnInit } from '@angular/core';
import { ElementRef, inject, Directive, HostListener, Input } from '@angular/core';
import { NgControl } from '@angular/forms';

export interface CurrencyMaskConfig {
  align: 'left' | 'right';
  allowNegative: boolean;
  allowZero: boolean;
  allowDecimal: boolean;
  decimal: string;
  precision: number;
  prefix: string;
  suffix: string;
  thousands: string;
  nullable: boolean;
  min: number | null;
  max: number | null;
  minLength: number | null;
  maxLength: number | null;
}

const DEFAULT_CONFIG: CurrencyMaskConfig = {
  align: 'left',
  allowNegative: true,
  allowZero: true,
  allowDecimal: true,
  decimal: '.',
  precision: 2,
  prefix: '',
  suffix: '',
  thousands: ',',
  nullable: false,
  min: null,
  max: null,
  minLength: null,
  maxLength: null,
};

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[currencyMask]',
})
export class CurrencyMaskDirective implements OnInit {
  elementRef = inject(ElementRef);
  control = inject(NgControl, { optional: true });
  @Input('currencyMask') config: Partial<CurrencyMaskConfig> = {};

  private mergedConfig: CurrencyMaskConfig;
  private readonly el: HTMLInputElement;
  private isFocused = false;

  constructor() {
    this.el = this.elementRef.nativeElement;
    this.mergedConfig = { ...DEFAULT_CONFIG };
  }

  ngOnInit(): void {
    this.mergedConfig = { ...DEFAULT_CONFIG, ...this.config };

    if (!this.mergedConfig.allowDecimal) {
      this.mergedConfig.precision = 0;
    }
    if (this.el.type !== 'text') {
      this.el.type = 'text';
    }
    this.el.style.textAlign = this.mergedConfig.align;

    // ✅ Handle external formControl updates safely
    this.control?.control?.valueChanges.subscribe((val) => {
      if (!this.isFocused && typeof val === 'number') {
        this.el.value = this.format(val.toString());
      }
    });

    // ✅ Initial formatting
    if (this.control?.control?.value !== null) {
      const VAL = this.control?.control?.value;
      this.el.value = this.format(VAL.toString());
    }
  }

  /** Format number into masked string **/
  private format(value: string): string {
    const CGF = this.mergedConfig;
    if (!value) {
      return CGF.nullable ? '' : CGF.prefix + CGF.suffix;
    }

    const DISALLOWED = CGF.allowDecimal ? new RegExp(`[^0-9\\${CGF.decimal}-]`, 'g') : /[^0-9-]/g;
    let clean = value.replace(DISALLOWED, '');

    const TYPED_NEGATIVE = CGF.allowNegative && clean.trim().startsWith('-');
    clean = clean.replace(/-/g, '');
    if (TYPED_NEGATIVE) {
      clean = '-' + clean;
    }

    if (CGF.allowDecimal && clean.includes(CGF.decimal)) {
      const IDX = clean.indexOf(CGF.decimal);
      clean =
        clean.slice(0, IDX + 1) +
        clean.slice(IDX + 1).replace(new RegExp(`\\${CGF.decimal}`, 'g'), '');
    }

    const PARTS = CGF.allowDecimal ? clean.split(CGF.decimal) : [clean];
    let integer = PARTS[0] || '';
    let decimals = CGF.allowDecimal ? PARTS[1] || '' : '';

    if (!CGF.allowNegative) {
      integer = integer.replace('-', '');
    }
    const NEGATIVE = CGF.allowNegative && integer.startsWith('-');
    integer = integer.replace('-', '');

    integer = integer.replace(new RegExp(`\\${CGF.thousands}`, 'g'), '');

    // Enforce maxLength before formatting
    if (CGF.maxLength !== null && CGF.maxLength >= 0) {
      integer = integer.substring(0, CGF.maxLength);
    }

    // Add thousands separators
    integer = integer.replace(/\B(?=(\d{3})+(?!\d))/g, CGF.thousands);

    // ✅ Proper decimal handling
    if (CGF.allowDecimal) {
      const EFFECTIVE_PRECISION = Math.max(0, CGF.precision);
      decimals = decimals.substring(0, EFFECTIVE_PRECISION);
    } else {
      decimals = '';
    }

    let cleanValue = `${integer}${decimals ? CGF.decimal + decimals : ''}`;

    // Pad with zeros if minLength required
    if (CGF.minLength !== null && CGF.minLength > 0) {
      const [INT_PART, DEC_PART] = cleanValue.split(CGF.decimal);
      const PADDED_INT = (INT_PART || '').padStart(CGF.minLength, '0');
      cleanValue = PADDED_INT + (DEC_PART ? CGF.decimal + DEC_PART : '');
    }

    return `${NEGATIVE ? '-' : ''}${CGF.prefix}${cleanValue}${CGF.suffix}`;
  }

  /** Parse formatted string into number **/
  private parse(raw: string): number | null {
    const CGF = this.mergedConfig;
    if (!raw.trim()) {
      return CGF.nullable ? null : 0;
    }

    let val = raw
      .replace(CGF.prefix, '')
      .replace(CGF.suffix, '')
      .replace(new RegExp(`\\${CGF.thousands}`, 'g'), '')
      .replace(CGF.decimal, '.');

    if (!CGF.allowDecimal) {
      val = val.split('.')[0];
    }

    const IS_NEG = CGF.allowNegative && val.trim().startsWith('-');
    val = val.replace(/-/g, '');
    if (IS_NEG) {
      val = '-' + val;
    }

    let num = parseFloat(val);

    if (isNaN(num)) {
      return null;
    }
    if (!CGF.allowZero && num === 0) {
      return null;
    }
    if (CGF.min !== null && num < CGF.min) {
      num = CGF.min;
    }
    if (CGF.max !== null && num > CGF.max) {
      num = CGF.max;
    }

    // Truncate decimals when not allowed
    if (!CGF.allowDecimal) {
      num = Math.trunc(num);
    }

    // Enforce maxLength on integer part
    if (CGF.maxLength !== null && CGF.maxLength >= 0) {
      const IS_NEGATIVE = num < 0;
      const ABS_STR = Math.abs(num).toString();
      const [INT_PART, DEC_PART] = ABS_STR.split('.');
      if (INT_PART.length > CGF.maxLength) {
        const TRIMMED_INT = INT_PART.substring(0, CGF.maxLength);
        const REBUILT = DEC_PART && CGF.allowDecimal ? `${TRIMMED_INT}.${DEC_PART}` : TRIMMED_INT;
        num = parseFloat((IS_NEGATIVE ? '-' : '') + REBUILT);
      }
    }

    return num;
  }

  @HostListener('input')
  onInput(): void {
    const RAW = this.el.value;
    const CGF = this.mergedConfig;

    if (CGF.allowNegative && RAW.trim() === '-') {
      return;
    }

    if (CGF.allowDecimal && RAW.endsWith(CGF.decimal)) {
      return;
    }

    if (CGF.allowNegative && RAW.includes('-') && !RAW.startsWith('-')) {
      const WITHOUT_MINUS = RAW.replace(/-/g, '');
      this.el.value = '-' + WITHOUT_MINUS;
    }

    // Enforce integer maxLength live
    if (CGF.maxLength !== null && CGF.maxLength >= 0) {
      const DISALLOWED = CGF.allowDecimal ? new RegExp(`[^0-9\\${CGF.decimal}-]`, 'g') : /[^0-9-]/g;
      let clean = this.el.value.replace(DISALLOWED, '');
      const NEGATIVE = CGF.allowNegative && (clean.trim().startsWith('-') || clean.includes('-'));
      clean = clean.replace(/-/g, '');
      const PARTS = CGF.allowDecimal ? clean.split(CGF.decimal) : [clean];
      let integer = PARTS[0] || '';
      const DECIMALS = CGF.allowDecimal ? PARTS[1] || '' : '';
      if (integer.length > CGF.maxLength) {
        integer = integer.substring(0, CGF.maxLength);
        const HAD_DECIMAL =
          CGF.allowDecimal && (clean.includes(CGF.decimal) || RAW.includes(CGF.decimal));
        const REBUILT = `${NEGATIVE ? '-' : ''}${integer}${
          HAD_DECIMAL ? CGF.decimal : ''
        }${DECIMALS}`;
        this.el.value = REBUILT;
      }
    }

    const PARSED = this.parse(this.el.value);
    this.control?.control?.setValue(PARSED, { emitEvent: true });
  }

  @HostListener('focus')
  onFocus(): void {
    this.isFocused = true;
    const CGF = this.mergedConfig;
    if (CGF.allowNegative && this.el.value.trim() === '-') {
      return;
    }

    const RAW = this.parse(this.el.value);
    if (RAW === null) {
      this.el.value = '';
      return;
    }

    const STR = CGF.allowDecimal
      ? RAW.toString().replace('.', CGF.decimal)
      : Math.trunc(RAW).toString();
    this.el.value = STR;
  }

  @HostListener('blur')
  onBlur(): void {
    this.isFocused = false;
    this.el.value = this.format(this.el.value);
  }
}
